import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNotify } from "react-admin";
import { settings } from "app";
import { studentRequests } from "api/studentApi";
import { fetchBlobImageWithHeaders } from "server/download";
import { CLASS_ATTENDANCE_QR_FALLBACK_REFRESH_SECONDS } from "utils/constants";

// How long the returned code stays valid — the backend owns this value and exposes it through CORS
const EXPIRES_IN_SECONDS_HEADER = "X-Expires-In-Seconds";

// Rendered as a child of the dialog, which unmounts it on close — so the refresh loop
// starts and stops together with the dialog and needs no open/close prop of its own.
const AttendanceQrCode = ({ classId }) => {
  const notify = useNotify();
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const objectUrlRef = useRef(null);

  const url = `${settings.gatewayApi}/${studentRequests.getClassAttendanceQrCode(classId)}`;

  useEffect(() => {
    let active = true;
    let timeoutId;

    const revokeCurrentObjectUrl = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };

    const loadQrCode = async () => {
      try {
        const { blob, headers } = await fetchBlobImageWithHeaders(url);
        if (!active) return;

        if (blob) {
          const nextObjectUrl = URL.createObjectURL(blob);
          // Swap the source first, then release the previous one, so the image never blinks
          setQrCodeUrl(nextObjectUrl);
          revokeCurrentObjectUrl();
          objectUrlRef.current = nextObjectUrl;
        }

        // Reschedule after every response instead of using a fixed interval: the lifetime is
        // configured on the backend and can differ from one response to the next
        const expiresInSeconds = Number(headers.get(EXPIRES_IN_SECONDS_HEADER));
        const refreshInSeconds =
          expiresInSeconds > 0 ? expiresInSeconds : CLASS_ATTENDANCE_QR_FALLBACK_REFRESH_SECONDS;
        timeoutId = setTimeout(loadQrCode, refreshInSeconds * 1000);
      } catch (err) {
        if (!active) return;
        // Stop refreshing on the first failure, otherwise the error would be notified on every cycle
        active = false;
        notify("Неуспешно зареждане на QR кода. Затворете екрана и опитайте отново.", { type: "error" });
      }
    };

    loadQrCode();

    return () => {
      active = false;
      clearTimeout(timeoutId);
      revokeCurrentObjectUrl();
    };
  }, [url, notify]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, py: 2 }}>
      <Typography
        color="text.secondary"
        sx={{ textAlign: "center" }}
      >
        Студентите сканират кода със своите телефони. Кодът се обновява автоматично.
      </Typography>
      <Box
        sx={{
          width: "min(60vh, 60vw)",
          height: "min(60vh, 60vw)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {qrCodeUrl ? (
          <img
            src={qrCodeUrl}
            alt="QR код за присъствие"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <CircularProgress />
        )}
      </Box>
    </Box>
  );
};

export default AttendanceQrCode;
